import { FeatureFlagType } from 'ducks/config/reducers';
import {
  configDataSelector,
  configErrorSelector
} from 'ducks/config/selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { EFeatureFlags } from '_clean-code/types/featureFlags';
import {
  isDevEnvironment,
  isLocalEnvironment,
  isStageEnvironment
} from '_clean-code/utils/environments';

export type FeatureFlagsType<T> = {
  [key in EFeatureFlags]: T;
};

export const DefaultFeatureFlags: FeatureFlagsType<boolean> = {
  availability: false,
  countSensor: false,
  prodLegacy: false,
  reportSubscription: false,
  prodBasic: false,
  prodFull: false,
  automaticNominalSpeeds: false,
  nominalSpeedCalculator: false,
  prodV1: false,
  realtimeV2: false,
  realtimeV2Sku: false,
  tvModeV2: false,
  cycleMetrics: false,
  video: false,
  energyMetrics: false,
  productCountMetrics: false,
  vibrationMetrics: false,
  temperatureMetrics: false,
  pressureMetrics: false,
  operatorsTimeline: false,
  operatorsV2: false,
  alertsV2: false,
  infraAlerts: false,
  isAccessSuspended: false,
  qualityIssueGroups: false,
  aiAnalytics: false,
  tvModeConfigurableDisplay: false,
  skuDetection: false,
  tvModeSingleOperatorView: false,
  productionSettingsV2: false,
  tvModeStatusTimeSetting: false,
  lockReviewedProductionAdvancements: false,
  rolePermissions: false,
  stopClips: false,
  productionMetricAlerts: false
};

export function useFeatureFlags(machineId: number): FeatureFlagsType<boolean>;

export function useFeatureFlags(
  machineIds: number[]
): FeatureFlagsType<boolean>;

export function useFeatureFlags(): FeatureFlagsType<boolean>;

export function useFeatureFlags(
  machineId: number | number[] | undefined = undefined
): FeatureFlagsType<boolean> {
  const userConfig = useSelector(configDataSelector);
  if (userConfig.featureFlags === undefined) return DefaultFeatureFlags;
  return getFlags(
    userConfig.featureFlags as FeatureFlagsType<FeatureFlagType>,
    machineId
  );
}
export function useShowWorkOrders(): boolean {
  // only show  work orders if prodFull is enabled
  const { prodLegacy } = useFeatureFlags();
  return prodLegacy;
}

export function useShowProduction(): boolean {
  const { prodLegacy, prodBasic, prodFull } = useFeatureFlagsAnyEnabled();
  return prodLegacy || prodBasic || prodFull;
}

export function useShowLegacyProduction(): boolean {
  const { prodLegacy, prodFull } = useFeatureFlags();
  return prodLegacy || prodFull;
}

export function useShowProductionLegacy(): boolean {
  const { prodLegacy } = useFeatureFlags();
  return prodLegacy;
}

/**
 * Whether the company config (and therefore the feature flags) has settled.
 * Until it does, `useFeatureFlags` reports every flag as `false`, so callers that
 * would redirect on a disabled flag must wait instead of acting on that default.
 * A failed config request also counts as settled: flags stay off and the caller
 * falls back to its disabled behaviour rather than waiting forever.
 */
export function useFeatureFlagsLoaded(): boolean {
  const userConfig = useSelector(configDataSelector);
  const configError = useSelector(configErrorSelector);
  return userConfig.featureFlags !== undefined || !!configError;
}

export function useShowAiBuilder(): boolean {
  // always visible outside production; in production, gated by the aiAnalytics flag
  const { aiAnalytics } = useFeatureFlags();
  return (
    isDevEnvironment() ||
    isStageEnvironment() ||
    isLocalEnvironment() ||
    aiAnalytics
  );
}

export function useShowProductionBasicOrFull(): boolean {
  const { prodBasic, prodFull } = useFeatureFlagsAnyEnabled();
  return prodBasic || prodFull;
}

export function useFeatureFlagsAnyEnabled(): FeatureFlagsType<boolean> {
  const machinesStore = (store: any) => store.machines;
  const machineSelector = useSelector(machinesStore);
  const { machines } = machineSelector;
  const machineIds = machines.map((machine) => machine.id);
  const userConfig = useSelector(configDataSelector);
  if (userConfig.featureFlags === undefined) {
    return DefaultFeatureFlags;
  }
  const inputFeatureFlags = userConfig.featureFlags;
  return createFeatureFlagsObject((key) =>
    getAnyEnabled(machineIds, inputFeatureFlags[key] as FeatureFlagType)
  );
}

interface Machine {
  id: number;
  [key: string]: any;
}

const machinesSelector = (store: { machines: { machines: Machine[] } }) =>
  store.machines.machines;

export function useFeatureFlagsMachineIds(): FeatureFlagsType<number[]> {
  const machines = useSelector(machinesSelector, (prev, next) => {
    if (prev.length !== next.length) return false;
    return prev.every((machine, index) => machine.id === next[index].id);
  });

  const userConfig = useSelector(configDataSelector);

  const machineIds = useMemo(
    () => machines.map((machine) => machine.id),
    [machines]
  );

  const enabledMachineIds = useMemo(() => {
    if (!userConfig.featureFlags) {
      return createFeatureFlagsObject(() => []);
    }
    const inputFeatureFlags = userConfig.featureFlags;
    return createFeatureFlagsObject((key) =>
      getAllEnabled(machineIds, inputFeatureFlags[key] as FeatureFlagType)
    );
  }, [machineIds, userConfig.featureFlags]);

  return enabledMachineIds;
}

export function useAllMachinesWithProduction() {
  const { prodLegacy, prodBasic, prodFull } = useFeatureFlagsMachineIds();

  // Company-level `enabled` can be false while individual machines are opted in.
  return useMemo(
    () => [...new Set([...prodLegacy, ...prodBasic, ...prodFull])],
    [prodLegacy, prodBasic, prodFull]
  );
}

export function getFlags(
  featureFlags: FeatureFlagsType<FeatureFlagType>,
  machineId: number | number[] | undefined = undefined
): FeatureFlagsType<boolean> {
  if (machineId === undefined) {
    return createFeatureFlagsObject((key) => featureFlags[key]?.enabled);
  }
  if (Array.isArray(machineId)) {
    return createFeatureFlagsObject((key) =>
      getEnabled(machineId, featureFlags[key])
    );
  }
  if (typeof machineId === 'number') {
    return createFeatureFlagsObject((key) =>
      getEnabledByMachine(machineId, featureFlags[key])
    );
  }
  return DefaultFeatureFlags;
}
function getEnabledByMachine(machineId: number, flag: FeatureFlagType) {
  const machineException = flag?.machines?.find(
    (machine) => machine.id === machineId
  );
  if (machineException) return machineException.enabled;
  return flag?.enabled ?? false;
}

// All or nothing
function getEnabled(machineId: number[], flag: FeatureFlagType) {
  return (
    machineId.filter((id) => getEnabledByMachine(id, flag)).length ===
    machineId.length
  );
}

function getAnyEnabled(machineId: number[], flag: FeatureFlagType) {
  return machineId.some((id) => getEnabledByMachine(id, flag));
}

function getAllEnabled(machineId: number[], flag: FeatureFlagType) {
  return machineId.filter((id) => getEnabledByMachine(id, flag));
}

function createFeatureFlagsObject<T>(
  instanceFunction: (key: string) => T
): FeatureFlagsType<T> {
  return Object.keys(DefaultFeatureFlags).reduce(
    (acc, key) => ({
      ...acc,
      [key]: instanceFunction(key) ?? DefaultFeatureFlags[key]
    }),
    DefaultFeatureFlags
  ) as FeatureFlagsType<T>;
}
